import React, { useState } from 'react'
import { Col, Container, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { TableColumn } from 'react-data-table-component'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import {
  MeetingTemplate,
  useGetDefaultMeetingTemplateQuery,
  useGetMeetingTemplatesQuery,
  useUpdateMeetingTemplateMutation,
} from '../../../store/ai/meetingTemplate'
import { Button } from '../../../components/Button/Button'
import { UninstallMeetingTemplateModal } from './UninstallMeetingTemplateModal'
import { TagElement } from '../../../components/Tag/Tag'
import { useNavigate } from 'react-router-dom'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'

export const MeetingTemplateTab: React.FC = () => {
  const { tracked } = useTracking()
  const navigate = useNavigate()
  const { hasScope } = useZocksAuth()
  const hasDeleteRole = hasScope('**', 'meeting_template:delete', ['meeting_template:admin'])
  const hasUpdateRole = hasScope('**', 'meeting_template:update', ['meeting_template:admin'])

  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const { data: meetingTemplates, isFetching } = useGetMeetingTemplatesQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, includeInactive: true },
    { refetchOnMountOrArgChange: true },
  )
  const { data: defaultMeetingTemplate } = useGetDefaultMeetingTemplateQuery({ accountId, userId: currentUserId }, { refetchOnMountOrArgChange: true })
  const [updateMeetingTemplate, { isLoading: updateIsLoading }] = useUpdateMeetingTemplateMutation()

  const [selectedMeetingTemplate, setSelectedMeetingTemplate] = useState<MeetingTemplate | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { getQuotaValue } = useZocksQuota()
  const maxPresetsInMeetingTemplate = getQuotaValue('ai.maxPresetsInMeetingTemplate.value') ?? 0

  const columns: TableColumn<MeetingTemplate>[] = [
    {
      name: 'Meeting Type',
      selector: (row) => row.name,
      sortable: true,
      grow: 5,
      cell: (row) => (
        <div className='flex-grow-1 d-flex gap-3 align-items-center'>
          <div className='p-3 zocks-bg-color-primary-light rounded zocks-text-primary'>
            <i className='meetingTemplateIcon d-flex align-items-center bi bi-columns-gap fs-5' />
          </div>
          <div className='button-link-medium text-decoration-none'>{row.name}</div>
        </div>
      ),
    },
    {
      name: 'Presets',
      wrap: true,
      grow: 0,
      selector: (row) => `${row.presets.length}/${maxPresetsInMeetingTemplate}`,
      sortable: true,
      minWidth: '7rem',
    },
    {
      name: 'Description',
      wrap: true,
      grow: 4,
      selector: (row) => row.detectionConfig.description,
      sortable: true,
    },
    {
      name: 'Tags',
      wrap: true,
      grow: 3,
      selector: (row) => row.isDefault,
      sortable: true,
      cell: (row) => (
        <div className='d-flex gap-2 flex-wrap'>
          {row.isDefault ? <TagElement>Account Default</TagElement> : null}
          {defaultMeetingTemplate?.isUserDefault && defaultMeetingTemplate?.id === row.id ? <TagElement>My Default</TagElement> : null}
          {row.fromSystemMeetingTemplate ? <TagElement>System</TagElement> : null}
        </div>
      ),
    },
    {
      grow: 0,
      name: 'Active',
      sortable: true,
      right: true,
      button: true,
      selector: (row) => row.isActive,
      cell: (row) => {
        return (
          <>
            <OverlayTrigger
              trigger={row.isDefault ? ['hover', 'focus'] : []}
              overlay={<Tooltip id='default-mt-tooltip'>The default meeting type cannot be deactivated.</Tooltip>}
            >
              <div className='d-flex align-items-center gap-2 px-3'>
                <Form.Switch
                  checked={row.isActive}
                  onChange={() => handleActivateMeetingTemplate(row)}
                  disabled={row.isDefault || updateIsLoading || !hasUpdateRole}
                />
              </div>
            </OverlayTrigger>
          </>
        )
      },
    },
    {
      grow: 4,
      right: true,
      cell: (row) => {
        return (
          <div className='d-flex gap-3 align-items-center flex-grow-1 ps-3'>
            {!row.fromSystemMeetingTemplate ? (
              <OverlayTrigger
                trigger={row.isDefault ? ['hover', 'focus'] : []}
                overlay={<Tooltip id='default-mt-tooltip'>The default meeting type cannot be deleted.</Tooltip>}
              >
                <div className='d-flex align-items-center gap-2'>
                  <Button className='p-2' variant='primaryGhost' onClick={() => handleDeleteMeetingTemplate(row)} disabled={row.isDefault || !hasDeleteRole}>
                    <i className='bi bi-trash' />
                  </Button>
                </div>
              </OverlayTrigger>
            ) : null}
            <Button className='p-2' variant='primaryGhost' onClick={() => handleShowMeetingTemplate(row)}>
              <i className='bi bi-gear' />
            </Button>
            <Button className='p-2' variant='primaryGhost' onClick={() => handleCopyMeetingTemplate(row)}>
              <i className='bi bi-copy'></i>
            </Button>
          </div>
        )
      },
    },
  ]

  const _handleActivateMeetingTemplate = (meetingTemplate: MeetingTemplate) => {
    const { isActive, id: meetingTemplateId } = meetingTemplate
    updateMeetingTemplate({ accountId, meetingTemplateId, payload: { isActive: !isActive } })
  }

  const handleActivateMeetingTemplate = tracked(
    _handleActivateMeetingTemplate,
    {},
    {
      eventNameExtractor: (meetingTemplate: MeetingTemplate) => {
        const { isActive } = meetingTemplate
        return isActive ? TrackEvent.MEETING_TEMPLATE_DEACTIVATED : TrackEvent.MEETING_TEMPLATE_ACTIVATED
      },
      eventDetailsExtractor: (meetingTemplate: MeetingTemplate) => {
        const { id: meetingTemplateId } = meetingTemplate
        return {
          meetingTemplateId,
        }
      },
    },
  )

  const handleDeleteModalHide = () => {
    setSelectedMeetingTemplate(null)
    setShowDeleteModal(false)
  }

  const handleDeleteMeetingTemplate = (meetingTemplate: MeetingTemplate) => {
    setSelectedMeetingTemplate(meetingTemplate)
    setShowDeleteModal(true)
  }

  const handleShowMeetingTemplate = (meetingTemplate: MeetingTemplate) => {
    navigate(`/workspace/setup/meeting-types/meeting-type/details/${meetingTemplate.id}/presets`)
  }

  const handleAddNewMeetingTemplate = () => {
    navigate(`/workspace/setup/meeting-types/meeting-type/create/presets`)
  }

  const handleCopyMeetingTemplate = (meetingTemplate: MeetingTemplate) => {
    navigate(`/workspace/setup/meeting-types/meeting-type/copy/${meetingTemplate.id}/presets`)
  }

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  return (
    <Container fluid>
      {showDeleteModal && selectedMeetingTemplate ? (
        <UninstallMeetingTemplateModal show={showDeleteModal} onHide={handleDeleteModalHide} meetingTemplate={selectedMeetingTemplate} />
      ) : null}
      <Row className='mb-4'>
        <Col>
          <ScopedBlock
            scope={{
              scope: 'meeting_template:create',
              superScope: ['meeting_template:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Button variant='primary' onClick={handleAddNewMeetingTemplate}>
              <i className='bi bi-plus' />
              Add new meeting type
            </Button>
          </ScopedBlock>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={meetingTemplates?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={meetingTemplates?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
